import { create } from 'zustand';
import type { StoreState, WebSocketEvent } from '../types';
import { apiClient } from '../services/api';

export const useStore = create<StoreState>((set) => ({
  // Initial State
  personas: [],
  selectedPersona: null,
  personasLoading: false,
  personasTotalCount: 0,
  personasPage: 1,
  personasPageSize: 50,

  systemStatus: null,
  healthStatus: null,

  wsConnected: false,
  wsStatus: 'disconnected',
  recentEvents: [],

  devices: [],
  selectedDevice: null,
  devicesLoading: false,

  detections: [],
  selectedDetection: null,
  detectionsLoading: false,
  detectionDrawerOpened: false,
  detectionsTotalCount: 0,
  detectionsPage: 1,
  detectionsPageSize: 50,
  detectionsProcessingState: 'all',

  personaDetections: [],
  personaDetectionsCount: 0,
  personaDetectionsLoading: false,

  sensorObservations: [],
  sensorObservationsLoading: false,

  // Setters
  setPersonas: (personas) => set({ personas }),
  setSelectedPersona: (persona) => set({ selectedPersona: persona }),
  setPersonasLoading: (loading) => set({ personasLoading: loading }),
  setPersonasTotalCount: (count) => set({ personasTotalCount: count }),
  setPersonasPage: (page) => set({ personasPage: page }),
  setPersonasPageSize: (size) => set({ personasPageSize: size }),

  setSystemStatus: (status) => set({ systemStatus: status }),
  setHealthStatus: (health) => set({ healthStatus: health }),

  setWsConnected: (connected) => set({ wsConnected: connected }),
  setWsStatus: (status) => set({ wsStatus: status }),

  addRecentEvent: (event: WebSocketEvent) => {
    set((state) => ({
      recentEvents: [event, ...state.recentEvents].slice(0, 50),
    }));
  },

  setDevices: (devices) => set({ devices }),
  setSelectedDevice: (device) => set({ selectedDevice: device }),
  setDevicesLoading: (loading) => set({ devicesLoading: loading }),

  setDetections: (detections) => set({ detections }),
  setSelectedDetection: (detection) => set({ selectedDetection: detection }),
  setDetectionsLoading: (loading) => set({ detectionsLoading: loading }),
  setDetectionDrawerOpened: (opened) => set({ detectionDrawerOpened: opened }),
  setDetectionsTotalCount: (count) => set({ detectionsTotalCount: count }),
  setDetectionsPage: (page) => set({ detectionsPage: page }),
  setDetectionsPageSize: (size) => set({ detectionsPageSize: size }),
  setDetectionsProcessingState: (state) => set({ detectionsProcessingState: state }),

  openDetectionDrawer: (detection) => set({
    selectedDetection: detection,
    detectionDrawerOpened: true
  }),

  closeDetectionDrawer: () => set({
    detectionDrawerOpened: false,
    selectedDetection: null
  }),

  setPersonaDetections: (detections) => set({ personaDetections: detections }),
  setPersonaDetectionsCount: (count) => set({ personaDetectionsCount: count }),
  setPersonaDetectionsLoading: (loading) => set({ personaDetectionsLoading: loading }),
  clearPersonaDetections: () => set({ personaDetections: [], personaDetectionsCount: 0 }),

  setSensorObservations: (observations) => set({ sensorObservations: observations }),
  setSensorObservationsLoading: (loading) => set({ sensorObservationsLoading: loading }),

  // Fetch Actions
  fetchPersonas: async (filters, showLoading = true) => {
    if (showLoading) set({ personasLoading: true });
    try {
      const state = useStore.getState();
      const response = await apiClient.getPersonas({
        limit: state.personasPageSize,
        offset: (state.personasPage - 1) * state.personasPageSize,
        ...filters,
      });
      set({
        personas: response.personas,
        personasTotalCount: response.count,
        personasLoading: false
      });
    } catch (error) {
      set({ personasLoading: false });
      throw error;
    }
  },

  fetchPersona: async (id: string) => {
    set({ personasLoading: true });
    try {
      const persona = await apiClient.getPersona(id);
      set({ selectedPersona: persona, personasLoading: false });
    } catch (error) {
      set({ personasLoading: false });
      throw error;
    }
  },

  fetchSystemStatus: async () => {
    try {
      const [status, health] = await Promise.all([
        apiClient.getSystemStatus(),
        apiClient.getHealth(),
      ]);
      set({ systemStatus: status, healthStatus: health });
    } catch (error) {
      throw error;
    }
  },

  fetchDevices: async (filters) => {
    set({ devicesLoading: true });
    try {
      const response = await apiClient.getDevices(filters);
      set({ devices: response.devices, devicesLoading: false });
    } catch (error) {
      set({ devicesLoading: false });
      throw error;
    }
  },

  fetchDevice: async (sensorId: string) => {
    set({ devicesLoading: true });
    try {
      const device = await apiClient.getDevice(sensorId);
      set({ selectedDevice: device, devicesLoading: false });
    } catch (error) {
      set({ devicesLoading: false });
      throw error;
    }
  },

  fetchDetections: async (filters) => {
    set({ detectionsLoading: true });
    try {
      const state = useStore.getState();
      const response = await apiClient.getDetections({
        limit: state.detectionsPageSize,
        offset: (state.detectionsPage - 1) * state.detectionsPageSize,
        processing_state: state.detectionsProcessingState,
        ...filters,
      });
      set({
        detections: response.detections,
        detectionsTotalCount: response.count,
        detectionsLoading: false
      });
    } catch (error) {
      set({ detectionsLoading: false });
      throw error;
    }
  },

  fetchPersonaDetections: async (personaId: string) => {
    set({ personaDetectionsLoading: true });
    try {
      const response = await apiClient.getDetections({
        persona_id: personaId,
        limit: 100,
        offset: 0,
        processing_state: 'all',
      });
      set({
        personaDetections: response.detections,
        personaDetectionsCount: response.count,
        personaDetectionsLoading: false
      });
    } catch (error) {
      set({ personaDetectionsLoading: false });
      throw error;
    }
  },

  fetchSensorObservations: async () => {
    set({ sensorObservationsLoading: true });
    try {
      const response = await apiClient.getRecentObservations();
      set({ sensorObservations: response.observations, sensorObservationsLoading: false });
    } catch (error) {
      set({ sensorObservationsLoading: false });
      throw error;
    }
  },
}));
